/**
 * iter98 / Session E — Employee Admin login (email + password only).
 *
 * Dedicated login page for the employee-admin account at
 * ``/admin-login``.  Talks to the strictly-scoped
 * ``POST /api/auth/admin-login`` backend which only accepts
 * ``role="employee_admin"`` accounts and NEVER offers a 2FA step.
 *
 * The Super Admin continues to sign in through the normal customer
 * ``/login`` flow (which enforces their TOTP 2FA).  This file must not
 * expose any Super Admin surface.
 */
import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

export default function AdminLogin() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/admin-login", { email, password });
      localStorage.setItem("sdf_token", data.access_token);
      setUser(data.user);
      toast.success("Signed in as Employee Admin");
      // Land on the public homepage — the Check Orders menu item is the
      // only administrative surface visible to this role.
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err) || "Invalid email or password");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-12 bg-brand-cream">
      <div
        className="w-full max-w-md bg-white border border-brand-line rounded-2xl p-8 shadow-sm"
        data-testid="admin-login-card"
      >
        <div className="text-center mb-8">
          <h1 className="font-serif text-2xl text-brand-dark">Employee Admin Sign in</h1>
        </div>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="text-xs font-medium text-brand-muted">Email</label>
            <input
              type="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input-field mt-1"
              placeholder="you@example.com"
              data-testid="admin-login-email"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-brand-muted">Password</label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input-field mt-1"
              placeholder="••••••••"
              data-testid="admin-login-password"
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="btn-primary w-full disabled:opacity-60"
            data-testid="admin-login-submit"
          >
            {submitting ? "Signing in…" : "Sign in"}
          </button>
        </form>
        <p className="text-xs text-brand-muted text-center mt-6">
          Are you a customer? <Link to="/login" className="text-brand-chili">Sign in here</Link>
        </p>
      </div>
    </div>
  );
}
